import React, { useState, useEffect, useRef } from 'react';

// Reusable confirm modal (matches ExploreAdmin pattern — modern, dark-mode aware, no native browser confirm)
const ConfirmModal = ({ open, isDarkMode, title, message, confirmLabel = 'Delete', confirmTone = 'danger', onConfirm, onCancel }) => {
  if (!open) return null;
  const btnColor = confirmTone === 'danger' ? 'bg-red-600 hover:bg-red-500' : 'bg-purple-600 hover:bg-purple-500';
  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center px-4" aria-modal="true" role="dialog">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onCancel} />
      <div className={`relative w-full max-w-md rounded-2xl shadow-2xl p-6 ${isDarkMode ? 'bg-gray-800 text-white border border-gray-700' : 'bg-white text-gray-900'}`}>
        <h3 className="text-lg font-semibold">{title}</h3>
        <p className={`mt-2 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{message}</p>
        <div className="flex gap-2 justify-end mt-5">
          <button onClick={onCancel} className={`px-4 py-2 rounded-lg text-sm font-medium ${isDarkMode ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>Cancel</button>
          <button onClick={onConfirm} className={`px-4 py-2 rounded-lg text-sm font-semibold text-white ${btnColor}`}>{confirmLabel}</button>
        </div>
      </div>
    </div>
  );
};

const formatBytes = (n) => {
  if (!n || n < 1024) return `${n || 0} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(2)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
};

const SoundLibrary = ({ API, adminToken, isDarkMode, showNotification }) => {
  const authHeaders = { Authorization: `Bearer ${adminToken}` };

  const [folders, setFolders] = useState([]);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(false);

  // settings + disk usage (live)
  const [settings, setSettings] = useState({
    max_upload_mb: 10, disk_quota_gb: 10, used_bytes: 0, used_gb: 0,
    quota_bytes: 0, files_count: 0, hard_max_per_file_mb: 50, hard_max_quota_gb: 50,
  });

  // folder create / rename
  const [newFolderName, setNewFolderName] = useState('');
  const [renamingFolderId, setRenamingFolderId] = useState(null);
  const [renameDraft, setRenameDraft] = useState('');
  const renameInputRef = useRef(null);

  // upload
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null); // { current, total, filename }

  // settings editor — now a tab instead of a modal
  const [settingsDraft, setSettingsDraft] = useState({ max_upload_mb: 10, disk_quota_gb: 10 });
  // active tab id: a folder id, or 'settings', or null (when no folders + not on settings)
  const [activeTabId, setActiveTabId] = useState(null);
  // new-folder inline input shown via "+" button
  const [showNewFolderInput, setShowNewFolderInput] = useState(false);

  // confirm modal
  const [confirmModal, setConfirmModal] = useState({ open: false });
  const askConfirm = (cfg) => setConfirmModal({ open: true, confirmLabel: 'Delete', confirmTone: 'danger', ...cfg });
  const closeConfirm = () => setConfirmModal((s) => ({ ...s, open: false }));

  // audio preview — store blob URLs per asset id (cleaned up on unmount)
  const [previewUrls, setPreviewUrls] = useState({}); // assetId -> blobUrl
  const [previewLoading, setPreviewLoading] = useState({}); // assetId -> bool
  const playingIdRef = useRef(null);
  const audioRef = useRef(null);

  // focus rename input without scroll jump
  useEffect(() => {
    if (renamingFolderId && renameInputRef.current) {
      try {
        renameInputRef.current.focus({ preventScroll: true });
        renameInputRef.current.select && renameInputRef.current.select();
      } catch {}
    }
  }, [renamingFolderId]);

  // cleanup blob URLs on unmount
  useEffect(() => {
    return () => {
      Object.values(previewUrls).forEach((url) => {
        try { URL.revokeObjectURL(url); } catch {}
      });
    };
    // eslint-disable-line
  }, []);

  // === Load functions ===
  const loadFolders = async () => {
    try {
      const res = await fetch(`${API}/admin/sound-library/folders`, { headers: authHeaders });
      if (res.ok) {
        const data = await res.json();
        setFolders(data.folders || []);
      }
    } catch (e) { /* non-critical */ }
  };

  const loadSettings = async () => {
    try {
      const res = await fetch(`${API}/admin/sound-library/settings`, { headers: authHeaders });
      if (res.ok) {
        const data = await res.json();
        setSettings(data);
        setSettingsDraft({ max_upload_mb: data.max_upload_mb, disk_quota_gb: data.disk_quota_gb });
      }
    } catch (e) {}
  };

  const loadAssets = async (folderId) => {
    if (!folderId) { setAssets([]); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/sound-library/folders/${folderId}/assets`, { headers: authHeaders });
      if (res.ok) {
        const data = await res.json();
        setAssets(data.assets || []);
      }
    } catch (e) { showNotification('Failed to load sounds', 'error'); }
    setLoading(false);
  };

  // Derived: the currently-active folder object (or null if "settings" / no folders)
  const selectedFolder = activeTabId && activeTabId !== 'settings'
    ? (folders.find((f) => f.id === activeTabId) || null)
    : null;

  useEffect(() => { loadFolders(); loadSettings(); }, []); // eslint-disable-line

  // Auto-select the first folder once folders load (if no tab chosen yet)
  useEffect(() => {
    if (!activeTabId && folders.length > 0) {
      setActiveTabId(folders[0].id);
    }
    // If active folder got deleted, fall back to settings or first remaining folder
    if (activeTabId && activeTabId !== 'settings' && !folders.find((f) => f.id === activeTabId)) {
      setActiveTabId(folders.length > 0 ? folders[0].id : 'settings');
    }
  }, [folders]); // eslint-disable-line

  useEffect(() => {
    if (selectedFolder) loadAssets(selectedFolder.id);
    else setAssets([]);
  }, [selectedFolder?.id]); // eslint-disable-line

  // === Folder actions ===
  const createFolder = async () => {
    const name = newFolderName.trim();
    if (!name) return;
    try {
      const res = await fetch(`${API}/admin/sound-library/folders`, {
        method: 'POST', headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ name }),
      });
      if (res.ok) {
        const data = await res.json();
        setNewFolderName(''); setShowNewFolderInput(false);
        showNotification('Folder created', 'success');
        await loadFolders();
        if (data.folder?.id) setActiveTabId(data.folder.id);
      } else {
        const err = await res.json().catch(() => ({}));
        showNotification(err.detail || 'Failed to create folder', 'error');
      }
    } catch (e) { showNotification('Failed to create folder', 'error'); }
  };

  const renameFolder = async (folderId) => {
    const name = renameDraft.trim();
    if (!name) { setRenamingFolderId(null); return; }
    try {
      const res = await fetch(`${API}/admin/sound-library/folders/${folderId}`, {
        method: 'PUT', headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ name }),
      });
      if (res.ok) {
        setRenamingFolderId(null); setRenameDraft('');
        showNotification('Folder renamed', 'success');
        await loadFolders();
        if (selectedFolder?.id === folderId) await loadAssets(folderId);
      } else {
        const err = await res.json().catch(() => ({}));
        showNotification(err.detail || 'Failed to rename', 'error');
      }
    } catch (e) { showNotification('Failed to rename', 'error'); }
  };

  const requestDeleteFolder = (folder) => {
    if ((folder.asset_count || 0) > 0) {
      showNotification(`Cannot delete "${folder.name}" — ${folder.asset_count} sound(s) inside. Delete them first.`, 'warning');
      return;
    }
    askConfirm({
      title: 'Delete folder?',
      message: `Remove the "${folder.name}" folder? This cannot be undone.`,
      onConfirm: async () => {
        closeConfirm();
        try {
          const res = await fetch(`${API}/admin/sound-library/folders/${folder.id}`, { method: 'DELETE', headers: authHeaders });
          if (res.ok) {
            showNotification('Folder deleted', 'success');
            if (activeTabId === folder.id) {
              setActiveTabId(null); // useEffect on folders will pick the next available tab
              setAssets([]);
            }
            await loadFolders();
          } else {
            const err = await res.json().catch(() => ({}));
            showNotification(err.detail || 'Failed to delete', 'error');
          }
        } catch (e) { showNotification('Failed to delete', 'error'); }
      },
    });
  };

  // === Upload ===
  const handleUpload = async (fileList) => {
    if (!selectedFolder) { showNotification('Select a folder first', 'warning'); return; }
    const files = Array.from(fileList || []);
    if (files.length === 0) return;

    // Client-side MP3 filter
    const mp3Files = files.filter((f) => /\.mp3$/i.test(f.name));
    if (mp3Files.length === 0) {
      showNotification('Only MP3 files are allowed', 'error');
      return;
    }
    if (mp3Files.length < files.length) {
      showNotification(`${files.length - mp3Files.length} non-MP3 file(s) skipped`, 'warning');
    }

    setUploading(true);
    let successCount = 0;
    let errorMessages = [];

    for (let i = 0; i < mp3Files.length; i++) {
      const file = mp3Files[i];
      setUploadProgress({ current: i + 1, total: mp3Files.length, filename: file.name });
      try {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('name', file.name.replace(/\.mp3$/i, ''));
        const res = await fetch(`${API}/admin/sound-library/folders/${selectedFolder.id}/upload`, {
          method: 'POST', headers: authHeaders, body: fd,
        });
        if (res.ok) successCount++;
        else {
          const err = await res.json().catch(() => ({}));
          errorMessages.push(`${file.name}: ${err.detail || 'failed'}`);
        }
      } catch (e) {
        errorMessages.push(`${file.name}: upload error`);
      }
    }

    setUploading(false);
    setUploadProgress(null);

    if (successCount > 0) showNotification(`Uploaded ${successCount} sound${successCount === 1 ? '' : 's'}`, 'success');
    if (errorMessages.length > 0) {
      console.warn('Upload errors:', errorMessages);
      showNotification(`${errorMessages.length} file(s) failed (see console)`, 'error');
    }

    await loadAssets(selectedFolder.id);
    await loadFolders();
    await loadSettings();
  };

  // === Asset preview (fetch + blob URL with auth header — bypasses native audio header limitation) ===
  const togglePreview = async (asset) => {
    // Stop any currently playing audio
    if (audioRef.current) {
      try { audioRef.current.pause(); } catch {}
      audioRef.current = null;
      playingIdRef.current = null;
    }

    if (!previewUrls[asset.id]) {
      setPreviewLoading((prev) => ({ ...prev, [asset.id]: true }));
      try {
        const res = await fetch(`${API}${asset.url.replace(/^\/api/, '')}`, { headers: authHeaders });
        if (!res.ok) {
          setPreviewLoading((prev) => ({ ...prev, [asset.id]: false }));
          showNotification('Failed to load audio', 'error');
          return;
        }
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        setPreviewUrls((prev) => ({ ...prev, [asset.id]: url }));
        setPreviewLoading((prev) => ({ ...prev, [asset.id]: false }));
        const audio = new Audio(url);
        audioRef.current = audio;
        playingIdRef.current = asset.id;
        audio.onended = () => { playingIdRef.current = null; audioRef.current = null; };
        audio.play().catch(() => showNotification('Playback error', 'warning'));
      } catch (e) {
        setPreviewLoading((prev) => ({ ...prev, [asset.id]: false }));
        showNotification('Failed to load audio', 'error');
      }
    } else {
      const audio = new Audio(previewUrls[asset.id]);
      audioRef.current = audio;
      playingIdRef.current = asset.id;
      audio.onended = () => { playingIdRef.current = null; audioRef.current = null; };
      audio.play().catch(() => showNotification('Playback error', 'warning'));
    }
  };

  // === Copy URL ===
  const copyUrl = async (asset) => {
    try {
      await navigator.clipboard.writeText(asset.url);
      showNotification('URL copied to clipboard', 'success');
    } catch (e) {
      // fallback for older browsers
      try {
        const ta = document.createElement('textarea');
        ta.value = asset.url;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        showNotification('URL copied', 'success');
      } catch { showNotification('Copy failed — select manually', 'error'); }
    }
  };

  // === Delete asset ===
  const requestDeleteAsset = (asset) => {
    askConfirm({
      title: 'Delete this sound?',
      message: `Remove "${asset.name}"? This cannot be undone.`,
      onConfirm: async () => {
        closeConfirm();
        try {
          const res = await fetch(`${API}/admin/sound-library/assets/${asset.id}`, { method: 'DELETE', headers: authHeaders });
          if (res.ok) {
            showNotification('Sound deleted', 'success');
            await loadAssets(selectedFolder.id);
            await loadFolders();
            await loadSettings();
          } else showNotification('Failed to delete', 'error');
        } catch (e) { showNotification('Failed to delete', 'error'); }
      },
    });
  };

  // === Settings save ===
  const saveSettings = async () => {
    try {
      const res = await fetch(`${API}/admin/sound-library/settings`, {
        method: 'PUT', headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify(settingsDraft),
      });
      if (res.ok) {
        showNotification('Settings saved', 'success');
        await loadSettings();
      } else {
        const err = await res.json().catch(() => ({}));
        showNotification(err.detail || 'Failed to save', 'error');
      }
    } catch (e) { showNotification('Failed to save', 'error'); }
  };

  const usedPercent = settings.quota_bytes > 0
    ? Math.min(100, Math.round((settings.used_bytes / settings.quota_bytes) * 100))
    : 0;

  return (
    <div className={`rounded-lg shadow-sm border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} p-6`}>
      <div className="mb-4">
        <h2 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>🔊 Sound Library</h2>
        <p className={`text-sm mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          Upload MP3 sound files, organize in folders, copy URLs to use in Listen &amp; Learn / Listen &amp; Guess clips.
          <span className="ml-1 italic">Login-required playback — anonymous users get 401.</span>
        </p>
      </div>

      {/* Disk usage bar */}
      <div className={`mb-5 p-3 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50 border border-gray-200'}`}>
        <div className="flex items-center justify-between mb-1.5 text-sm">
          <span className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>
            📊 <span className="font-semibold">{settings.used_gb.toFixed(2)} GB</span> / {settings.disk_quota_gb} GB used
          </span>
          <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>
            {settings.files_count} file{settings.files_count === 1 ? '' : 's'} · max {settings.max_upload_mb} MB each
          </span>
        </div>
        <div className={`h-2 rounded-full overflow-hidden ${isDarkMode ? 'bg-gray-800' : 'bg-gray-200'}`}>
          <div
            className={`h-full transition-all ${usedPercent < 70 ? 'bg-green-500' : usedPercent < 90 ? 'bg-yellow-500' : 'bg-red-500'}`}
            style={{ width: `${usedPercent}%` }}
          />
        </div>
      </div>

      {/* Tab strip — one tab per folder, plus "+" (new folder) and ⚙️ Settings */}
      <div className={`border-b mb-4 ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
        <div className="flex items-end gap-1 overflow-x-auto pb-px">
          {folders.map((folder) => {
            const isActive = activeTabId === folder.id;
            const isRenaming = renamingFolderId === folder.id;
            return (
              <div key={folder.id} className="flex-shrink-0">
                {isRenaming ? (
                  <div className={`flex items-center gap-1 px-3 py-2 rounded-t-lg border border-b-0 ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-300'}`}>
                    <input type="text" value={renameDraft}
                      ref={renameInputRef}
                      onChange={(e) => setRenameDraft(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') renameFolder(folder.id); if (e.key === 'Escape') setRenamingFolderId(null); }}
                      className={`px-2 py-0.5 rounded text-sm border w-32 ${isDarkMode ? 'bg-gray-800 border-gray-500 text-white' : 'bg-white border-gray-300 text-gray-900'}`} />
                    <button onClick={() => renameFolder(folder.id)} className="px-2 py-0.5 bg-green-600 text-white rounded text-xs">✓</button>
                    <button onClick={() => setRenamingFolderId(null)} className={`px-2 py-0.5 rounded text-xs ${isDarkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-200 text-gray-700'}`}>✕</button>
                  </div>
                ) : (
                  <button
                    onClick={() => setActiveTabId(folder.id)}
                    className={`group relative flex items-center gap-2 px-4 py-2 rounded-t-lg text-sm font-medium border border-b-0 transition-colors whitespace-nowrap ${
                      isActive
                        ? (isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900 -mb-px')
                        : (isDarkMode ? 'bg-gray-800 border-transparent text-gray-400 hover:text-gray-200 hover:bg-gray-700' : 'bg-gray-100 border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-50')
                    }`}
                  >
                    <span>📁 {folder.name}</span>
                    <span className={`text-xs px-1.5 py-0.5 rounded-full ${isActive ? (isDarkMode ? 'bg-gray-600 text-gray-200' : 'bg-gray-200 text-gray-700') : (isDarkMode ? 'bg-gray-900 text-gray-400' : 'bg-white text-gray-500')}`}>
                      {folder.asset_count || 0}
                    </span>
                    {isActive && (
                      <span className="flex items-center gap-1 ml-1">
                        <span
                          onClick={(e) => { e.stopPropagation(); setRenamingFolderId(folder.id); setRenameDraft(folder.name); }}
                          className="text-xs cursor-pointer hover:opacity-75" title="Rename">✏️</span>
                        <span
                          onClick={(e) => { e.stopPropagation(); requestDeleteFolder(folder); }}
                          className={`text-xs ${(folder.asset_count || 0) > 0 ? 'opacity-30 cursor-not-allowed' : 'cursor-pointer hover:opacity-75'}`}
                          title={(folder.asset_count || 0) > 0 ? 'Delete sounds first' : 'Delete folder'}>🗑️</span>
                      </span>
                    )}
                  </button>
                )}
              </div>
            );
          })}

          {/* "+ New folder" tab */}
          {showNewFolderInput ? (
            <div className={`flex items-center gap-1 px-3 py-2 rounded-t-lg border border-b-0 flex-shrink-0 ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-300'}`}>
              <input type="text" value={newFolderName}
                autoFocus
                onChange={(e) => setNewFolderName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') createFolder(); if (e.key === 'Escape') { setShowNewFolderInput(false); setNewFolderName(''); } }}
                placeholder="Folder name"
                className={`px-2 py-0.5 rounded text-sm border w-32 ${isDarkMode ? 'bg-gray-800 border-gray-500 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'}`} />
              <button onClick={createFolder} className="px-2 py-0.5 bg-green-600 text-white rounded text-xs">✓</button>
              <button onClick={() => { setShowNewFolderInput(false); setNewFolderName(''); }}
                className={`px-2 py-0.5 rounded text-xs ${isDarkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-200 text-gray-700'}`}>✕</button>
            </div>
          ) : (
            <button
              onClick={() => setShowNewFolderInput(true)}
              className={`flex-shrink-0 px-3 py-2 rounded-t-lg text-sm font-medium border border-b-0 transition-colors whitespace-nowrap ${
                isDarkMode ? 'bg-gray-800 border-transparent text-gray-400 hover:text-green-400 hover:bg-gray-700' : 'bg-gray-100 border-transparent text-gray-600 hover:text-green-600 hover:bg-gray-50'
              }`}
              title="Create new folder"
            >
              ➕ New folder
            </button>
          )}

          <div className="flex-1" />

          {/* Settings tab (always rightmost) */}
          <button
            onClick={() => setActiveTabId('settings')}
            className={`flex-shrink-0 px-4 py-2 rounded-t-lg text-sm font-medium border border-b-0 transition-colors whitespace-nowrap ${
              activeTabId === 'settings'
                ? (isDarkMode ? 'bg-gray-700 border-gray-600 text-white -mb-px' : 'bg-white border-gray-300 text-gray-900 -mb-px')
                : (isDarkMode ? 'bg-gray-800 border-transparent text-gray-400 hover:text-gray-200 hover:bg-gray-700' : 'bg-gray-100 border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-50')
            }`}
          >
            ⚙️ Settings
          </button>
        </div>
      </div>

      {/* Tab content */}
      {activeTabId === 'settings' ? (
        /* ─── SETTINGS TAB ─── */
        <div className={`p-5 rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
          <h3 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>⚙️ Sound Library Settings</h3>

          <div className="mb-5">
            <label className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Max upload size per file: <span className="font-bold">{settingsDraft.max_upload_mb} MB</span>
            </label>
            <input type="range" min="1" max={settings.hard_max_per_file_mb} step="1"
              value={settingsDraft.max_upload_mb}
              onChange={(e) => setSettingsDraft({ ...settingsDraft, max_upload_mb: parseInt(e.target.value) || 1 })}
              className="w-full mt-2" />
            <div className={`flex justify-between text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              <span>1 MB</span><span>{settings.hard_max_per_file_mb} MB</span>
            </div>
          </div>

          <div className="mb-5">
            <label className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Total disk quota: <span className="font-bold">{settingsDraft.disk_quota_gb} GB</span>
            </label>
            <input type="range" min="1" max={settings.hard_max_quota_gb} step="1"
              value={settingsDraft.disk_quota_gb}
              onChange={(e) => setSettingsDraft({ ...settingsDraft, disk_quota_gb: parseInt(e.target.value) || 1 })}
              className="w-full mt-2" />
            <div className={`flex justify-between text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              <span>1 GB</span><span>{settings.hard_max_quota_gb} GB</span>
            </div>
            <p className={`text-xs mt-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Current usage: {settings.used_gb.toFixed(2)} GB across {settings.files_count} file(s)
            </p>
          </div>

          <button onClick={saveSettings}
            className="px-4 py-2 rounded-lg text-sm font-semibold text-white bg-purple-600 hover:bg-purple-500">
            💾 Save Settings
          </button>
        </div>
      ) : !selectedFolder ? (
        /* ─── EMPTY STATE (no folders yet) ─── */
        <div className={`p-12 rounded-lg border text-center ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-300' : 'bg-gray-50 border-gray-200 text-gray-600'}`}>
          <span className="text-6xl block mb-3">📁</span>
          <p className="text-lg font-medium">No folders yet</p>
          <p className="text-sm mt-1">Click <span className="font-semibold">➕ New folder</span> above to create your first folder.</p>
        </div>
      ) : (
        /* ─── FOLDER TAB (upload + assets) ─── */
        <div className={`p-4 rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
          <div className="flex items-center justify-between mb-3">
            <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>📁 {selectedFolder.name}</h3>
            <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              {assets.length} sound{assets.length === 1 ? '' : 's'}
            </span>
          </div>

          {/* Upload zone */}
          <label className={`block p-6 rounded-lg border-2 border-dashed cursor-pointer text-center mb-4 transition-colors ${
            uploading
              ? 'opacity-60 cursor-wait'
              : isDarkMode ? 'border-gray-500 hover:border-purple-400 bg-gray-800' : 'border-gray-300 hover:border-purple-500 bg-white'
          }`}>
            <input type="file" accept="audio/mpeg,audio/mp3,.mp3" multiple
              disabled={uploading}
              onChange={(e) => handleUpload(e.target.files)}
              className="hidden" />
            {uploading ? (
              <div>
                <span className="text-3xl block mb-2">⏳</span>
                {uploadProgress && (
                  <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Uploading {uploadProgress.current}/{uploadProgress.total}: <span className="font-mono text-xs">{uploadProgress.filename}</span>
                  </p>
                )}
              </div>
            ) : (
              <div>
                <span className="text-3xl block mb-2">⬆️</span>
                <p className={`text-sm font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>Drop MP3 files here or click to upload</p>
                <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>MP3 only · Max {settings.max_upload_mb} MB per file</p>
              </div>
            )}
          </label>

          {/* Asset list */}
          {loading ? (
            <p className={`text-center py-6 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Loading...</p>
          ) : assets.length === 0 ? (
            <p className={`text-center py-6 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>No sounds in this folder yet.</p>
          ) : (
            <div className="space-y-2 max-h-[600px] overflow-y-auto">
              {assets.map((asset) => {
                const isLoading = previewLoading[asset.id];
                return (
                  <div key={asset.id} className={`p-3 rounded-lg border flex items-center gap-3 ${isDarkMode ? 'bg-gray-600 border-gray-500' : 'bg-white border-gray-200'}`}>
                    <button onClick={() => togglePreview(asset)}
                      disabled={isLoading}
                      className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center transition-transform ${
                        isLoading ? 'bg-gray-400 cursor-wait' : 'bg-gradient-to-br from-purple-600 to-indigo-600 hover:scale-105 text-white'
                      }`}
                      aria-label="Play">
                      {isLoading ? '⏳' : (
                        <svg className="w-4 h-4 ml-0.5" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>
                      )}
                    </button>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium truncate ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{asset.name}</p>
                      <p className={`text-xs truncate font-mono ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{asset.url}</p>
                    </div>
                    <span className={`flex-shrink-0 text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      {formatBytes(asset.size_bytes)}
                    </span>
                    <button onClick={() => copyUrl(asset)}
                      className="text-xs px-2 py-1 rounded bg-blue-600 hover:bg-blue-500 text-white"
                      title="Copy URL to clipboard">📋 Copy</button>
                    <button onClick={() => requestDeleteAsset(asset)}
                      className="text-red-500 hover:text-red-400 text-sm flex-shrink-0" title="Delete">🗑️</button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      <ConfirmModal
        open={confirmModal.open}
        isDarkMode={isDarkMode}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmLabel={confirmModal.confirmLabel}
        confirmTone={confirmModal.confirmTone}
        onConfirm={confirmModal.onConfirm}
        onCancel={closeConfirm}
      />
    </div>
  );
};

export default SoundLibrary;
